"use client"

import { useState, useEffect } from "react"
import { DashboardLayout } from "../../../components/dashboard-layout"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../components/ui/card"
import { Progress } from "../../../components/ui/progress"
import { useTheme } from "../../../contexts/theme-provider"
import { BarChart3, Calendar, CheckCircle, Clock, TrendingUp, FileText, Sparkles } from "lucide-react"
import { useAuth } from "../../../contexts/auth-context"
import { handleGetQuota, handleGetMyUsage } from "../../../services/quota"
import { motion, AnimatePresence } from "framer-motion"

interface QuotaData {
  client_used: number
  client_limit: number
  client_remaining: number | null
  user_used: number
  user_limit: number
  user_remaining: number | null
}

interface MonthlyUsage {
  month: string
  reports: number
  percentage: number
}

export default function QuotaPage() {
  const { user } = useAuth()
  const { theme } = useTheme()
  const [quotaData, setQuotaData] = useState<QuotaData | null>(null)
  const [usageLogs, setUsageLogs] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [monthlyUsage, setMonthlyUsage] = useState<MonthlyUsage[]>([])
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
    if (user?.id) {
      fetchData()
    }
  }, [user?.id])

  const fetchData = async () => {
    if (!user?.id || !user?.access_token) {
      console.warn("No user or access token, skipping fetchData");
      setIsLoading(false);
      return;
    }
    try {
      setIsLoading(true)

      if (user?.id) {
        const quota = await handleGetQuota(user.id, user.access_token)
        setQuotaData(Array.isArray(quota) ? quota[0] : quota)
      }

      const usage = await handleGetMyUsage(user.access_token)
      setUsageLogs(usage.data)

      calculateMonthlyUsage(usage.data)
    } catch (error) {
      console.error("Error fetching data:", error)
      setQuotaData({
        client_used: 0,
        client_limit: 50,
        client_remaining: 50,
        user_used: 0,
        user_limit: 50,
        user_remaining: 50,
      })
      setUsageLogs([])
    } finally {
      setIsLoading(false)
    }
  }

  const calculateMonthlyUsage = (logs: any[]) => {
    const monthlyCounts: { [key: string]: number } = {}

    logs.forEach((log) => {
      if (log.created_at) {
        const date = new Date(log.created_at)
        const monthYear = `${date.toLocaleString("default", { month: "long" })} ${date.getFullYear()}`
        monthlyCounts[monthYear] = (monthlyCounts[monthYear] || 0) + 1
      }
    })

    const maxReports = quotaData?.user_limit || 50
    const usageData: MonthlyUsage[] = Object.entries(monthlyCounts)
      .sort((a, b) => {
        const dateA = new Date(a[0])
        const dateB = new Date(b[0])
        return dateB.getTime() - dateA.getTime()
      })
      .slice(0, 6)
      .map(([month, reports]) => ({
        month,
        reports,
        percentage: Math.min((reports / maxReports) * 100, 100),
      }))

    setMonthlyUsage(usageData)
  }

  if (!mounted) return null

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className={`min-h-screen p-4 md:p-8 flex items-center justify-center ${
          theme === "dark" ? "bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" : "bg-gradient-to-br from-slate-50 via-white to-blue-50"
        }`}>
          <div className="text-center">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 2, repeat: Number.POSITIVE_INFINITY, ease: "linear" }}
              className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-4"
            />
            <p className={theme === "dark" ? "text-slate-300" : "text-slate-600 dark:text-slate-400"}>
              Loading usage statistics...
            </p>
          </div>
        </div>
      </DashboardLayout>
    )
  }

  // ✅ Safe fallbacks for quotaData
  const totalReports = quotaData?.user_used ?? 0
  const maxQuota = quotaData?.user_limit ?? 50
  const remainingQuota = quotaData?.user_remaining ?? maxQuota
  const currentMonth = new Date().toLocaleString("default", { month: "long", year: "numeric" })
  const reportsThisMonth = usageLogs.filter((log) => {
    if (!log.created_at) return false
    const logDate = new Date(log.created_at)
    const logMonth = logDate.toLocaleString("default", { month: "long", year: "numeric" })
    return logMonth === currentMonth
  }).length

  const usagePercentage = Math.min((reportsThisMonth / maxQuota) * 100, 100)
  const resetDate = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 1)
  const daysUntilReset = Math.ceil((resetDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))

  return (
    <DashboardLayout>
      <div className={`min-h-screen p-4 md:p-8 ${
        theme === "dark" ? "bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" : "bg-gradient-to-br from-slate-50 via-white to-blue-50"
      }`}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-6xl mx-auto space-y-6 md:space-y-8"
        >
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
          >
            <div className="w-full sm:w-auto">
              <motion.h1
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className={`text-3xl md:text-4xl font-black bg-clip-text text-transparent mb-2 ${
                  theme === "dark" ? "bg-gradient-to-r from-white via-blue-100 to-purple-100" : "bg-gradient-to-r from-slate-900 via-blue-900 to-purple-900"
                }`}
              >
                Usage & Quota
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className={`font-medium text-sm md:text-base ${theme === "dark" ? "text-slate-300" : "text-slate-600 dark:text-slate-400"}`}
              >
                Track your report generation usage and remaining quota
              </motion.p>
            </div>
            <motion.div 
              initial={{ opacity: 0, x: 20 }} 
              animate={{ opacity: 1, x: 0 }} 
              transition={{ delay: 0.4 }}
              className="w-full sm:w-auto"
            >
            </motion.div>
          </motion.div>

          {/* Usage Overview Cards */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6"
          >
            {[
              {
                icon: BarChart3,
                title: "Total Reports Generated",
                value: totalReports,
                color: "from-blue-500 to-cyan-600",
                bgColor: theme === "dark" ? "from-blue-950/30 to-cyan-950/30" : "from-blue-50 dark:from-blue-900/30 to-cyan-50 dark:to-cyan-900/30",
              },
              {
                icon: Calendar,
                title: "Reports This Month",
                value: reportsThisMonth,
                color: "from-green-500 to-emerald-600",
                bgColor: theme === "dark" ? "from-green-950/30 to-emerald-950/30" : "from-green-50 dark:from-green-900/30 to-emerald-50 dark:to-emerald-900/30",
              },
              {
                icon: FileText,
                title: "Remaining Reports",
                value: remainingQuota,
                color: "from-purple-500 to-pink-600",
                bgColor: theme === "dark" ? "from-purple-950/30 to-pink-950/30" : "from-purple-50 dark:from-purple-900/30 to-pink-50 dark:to-pink-900/30",
              },
            ].map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.3 + index * 0.1, duration: 0.4 }}
                whileHover={{ scale: 1.05, y: -5 }}
              >
                <Card className={`bg-gradient-to-br ${stat.bgColor} border-0 shadow-lg md:shadow-xl ${
                  theme === "dark" ? "shadow-black/20" : "shadow-black/5"
                }`}>
                  <CardContent className="p-4 md:p-6">
                    <div className="flex items-center gap-3 md:gap-4">
                      <div
                        className={`w-12 h-12 md:w-16 md:h-16 bg-gradient-to-br ${stat.color} rounded-xl flex items-center justify-center shadow-md md:shadow-lg`}
                      >
                        <stat.icon className="h-5 w-5 md:h-6 md:w-6 text-white" />
                      </div>
                      <div>
                        <p
                          className={`text-xs md:text-sm font-bold mb-1 ${
                            theme === "dark" ? "text-slate-300" : "text-slate-700 dark:text-slate-200"
                          }`}
                        >
                          {stat.title}
                        </p>
                        <p className={`text-2xl md:text-3xl font-black bg-gradient-to-r ${stat.color} bg-clip-text text-transparent`}>
                          {stat.value}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>

          {/* Usage Progress */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.5 }}
          >
            <Card className={`backdrop-blur-xl shadow-lg md:shadow-xl border ${
              theme === "dark" ? "bg-slate-800/70 border-slate-700/50 shadow-black/20" : "bg-white/70 border-slate-200/50 shadow-black/5"
            }`}>
              <CardHeader className="p-4 md:p-6">
                <div className="flex items-center gap-3">
                  <motion.div
                    animate={{ rotate: [0, 360] }}
                    transition={{ duration: 4, repeat: Number.POSITIVE_INFINITY, ease: "linear" }}
                    className="w-6 h-6 md:w-8 md:h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center"
                  >
                    <Sparkles className="h-3 w-3 md:h-4 md:w-4 text-white" />
                  </motion.div>
                  <div>
                    <CardTitle className={`text-lg md:text-xl font-black ${theme === "dark" ? "text-white" : "text-slate-900 dark:text-white"}`}>
                      Monthly Usage Progress
                    </CardTitle>
                    <CardDescription className={`text-sm md:text-base font-medium ${theme === "dark" ? "text-slate-300" : "text-slate-600 dark:text-slate-400"}`}>
                      {reportsThisMonth} of {maxQuota} reports used this month
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4 md:space-y-6 p-4 md:p-6 pt-0">
                <div className="space-y-3">
                  <div className="flex justify-between text-xs md:text-sm">
                    <span className={`font-bold ${theme === "dark" ? "text-slate-300" : "text-slate-700 dark:text-slate-200"}`}>
                      Usage Progress
                    </span>
                    <span className={`font-black ${theme === "dark" ? "text-white" : "text-slate-900 dark:text-white"}`}>
                      {usagePercentage.toFixed(1)}%
                    </span>
                  </div>
                  <Progress
                    value={usagePercentage}
                    className={`h-3 md:h-4 rounded-full ${
                      usagePercentage >= 90
                        ? "[&>div]:bg-gradient-to-r [&>div]:from-red-500 [&>div]:to-orange-500"
                        : usagePercentage >= 75
                          ? "[&>div]:bg-gradient-to-r [&>div]:from-yellow-500 [&>div]:to-orange-500"
                          : "[&>div]:bg-gradient-to-r [&>div]:from-green-500 [&>div]:to-emerald-500"
                    }`}
                  />
                </div>

                <div
                  className={`flex flex-col sm:flex-row items-start sm:items-center justify-between text-xs md:text-sm gap-2 font-medium ${
                    theme === "dark" ? "text-slate-300" : "text-slate-600 dark:text-slate-400"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    <span>Resets in {daysUntilReset} days</span>
                  </div>
                  <span className="text-right">Next reset: {resetDate.toLocaleDateString()}</span>
                </div>

                <AnimatePresence>
                  {usagePercentage >= 75 && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      className={`p-3 md:p-4 rounded-xl border ${
                        usagePercentage >= 90
                          ? theme === "dark"
                            ? "bg-red-900/20 border-red-500/30"
                            : "bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-800"
                          : theme === "dark"
                            ? "bg-yellow-900/20 border-yellow-500/30"
                            : "bg-yellow-50 dark:bg-yellow-900/30 border-yellow-200 dark:border-yellow-800"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <CheckCircle
                          className={`h-4 w-4 md:h-5 md:w-5 ${
                            usagePercentage >= 90 ? "text-red-500 dark:text-red-400" : "text-yellow-500 dark:text-yellow-400"
                          }`}
                        />
                        <div>
                          <span
                            className={`text-xs md:text-sm font-bold ${
                              usagePercentage >= 90
                                ? theme === "dark"
                                  ? "text-red-400"
                                  : "text-red-800 dark:text-red-200"
                                : theme === "dark"
                                  ? "text-yellow-400"
                                  : "text-yellow-800 dark:text-yellow-200"
                            }`}
                          >
                            {usagePercentage >= 90
                              ? "You're approaching your monthly limit"
                              : "You're using your reports efficiently"}
                          </span>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </CardContent>
            </Card>
          </motion.div>

          {/* Usage History */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.5 }}
          >
            <Card className={`backdrop-blur-xl shadow-lg md:shadow-xl border ${
              theme === "dark" ? "bg-slate-800/70 border-slate-700/50 shadow-black/20" : "bg-white/70 border-slate-200/50 shadow-black/5"
            }`}>
              <CardHeader className="p-4 md:p-6">
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 md:w-8 md:h-8 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg flex items-center justify-center">
                    <TrendingUp className="h-3 w-3 md:h-4 md:w-4 text-white" />
                  </div>
                  <div>
                    <CardTitle className={`text-lg md:text-xl font-black ${theme === "dark" ? "text-white" : "text-slate-900 dark:text-white"}`}>
                      Monthly Usage History
                    </CardTitle>
                    <CardDescription className={`text-sm md:text-base font-medium ${theme === "dark" ? "text-slate-300" : "text-slate-600 dark:text-slate-400"}`}>
                      Your report generation trends over the past months
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-4 md:p-6 pt-0">
                <AnimatePresence mode="wait">
                  {monthlyUsage.length > 0 ? (
                    <motion.div
                      key="usage-data"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="space-y-4"
                    >
                      {monthlyUsage.map((month, index) => (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.1 }}
                          className="flex flex-col sm:flex-row items-start sm:items-center justify-between text-xs md:text-sm gap-2"
                        >
                          <span className={`font-bold ${theme === "dark" ? "text-slate-300" : "text-slate-600 dark:text-slate-400"}`}>
                            {month.month}
                          </span>
                          <div className="flex items-center gap-3 w-full sm:w-auto">
                            <div className="w-20 md:w-24 bg-gray-200 rounded-full h-2 dark:bg-slate-700">
                              <div
                                className={`h-2 rounded-full ${
                                  month.percentage >= 90
                                    ? "bg-gradient-to-r from-red-500 to-orange-500"
                                    : month.percentage >= 75
                                      ? "bg-gradient-to-r from-yellow-500 to-orange-500"
                                      : "bg-gradient-to-r from-green-500 to-emerald-500"
                                }`}
                                style={{ width: `${month.percentage}%` }}
                              />
                            </div>
                            <span
                              className={`font-black min-w-[2rem] text-right ${
                                theme === "dark" ? "text-white" : "text-slate-900 dark:text-white"
                              }`}
                            >
                              {month.reports}
                            </span>
                          </div>
                        </motion.div>
                      ))}
                    </motion.div>
                  ) : (
                    <motion.div
                      key="empty-state"
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.9 }}
                      className="text-center py-8 md:py-12 px-4"
                    >
                      <BarChart3 className={`h-12 w-12 md:h-16 md:w-16 mx-auto mb-3 md:mb-4 ${
                        theme === "dark" ? "text-slate-500" : "text-slate-400"
                      }`} />
                      <p className={`text-base md:text-lg font-bold ${
                        theme === "dark" ? "text-slate-400" : "text-slate-500 dark:text-slate-400"
                      }`}>
                        No usage history available
                      </p>
                      <p className={`text-xs md:text-sm mt-1 md:mt-2 ${
                        theme === "dark" ? "text-slate-500" : "text-slate-400"
                      }`}>
                        Generate your first report to see usage trends
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </CardContent>
            </Card>
          </motion.div>
        </motion.div>
      </div>
    </DashboardLayout>
  )
}